import { useEffect, useState } from 'react';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import { useUserRolePermissions } from 'hooks';
import { createSnack } from 'snackbar/snackbarSlice';
import { useEditCustomerAuthorizationMutation } from '../customerAuthorizationsEndpoints';

const INITIAL_VALIDATION = {
  address: '',
  address2: '',
  authorizationType: '',
  authorizationVolume: '',
  city: '',
  controlNumbers: [],
  customerName: '',
  phoneNumber: '',
  renewalVolume: '',
  state: '',
  zipCode: '',
};

const useCustomerAuthorizationEditor = ({
  customerAuthorization,
  isEditMode = true,
  onClose,
  onSaveSuccess,
}) => {
  const [customerAuth, setCustomerAuth] = useState({});
  const [validation, setValidation] = useState(INITIAL_VALIDATION);
  const [initialCustomerAuth, setInitialCustomerAuth] = useState({});
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  const { currentAffiliate } = useUserRolePermissions();
  const dispatch = useDispatch();
  const [submit, { isLoading: isSaving }] = useEditCustomerAuthorizationMutation();

  useEffect(() => {
    if (R.not(R.isEmpty(customerAuthorization))) {
      setCustomerAuth(customerAuthorization);
      setInitialCustomerAuth(customerAuthorization);
      setValidation({
        ...INITIAL_VALIDATION,
        controlNumbers: Array(customerAuthorization.controlNumbers?.length ?? 0).fill(''),
      });
    }
  }, [customerAuthorization]);

  const hasValidationErrors = R.any(
    value =>
      Array.isArray(value) ? R.any(R.complement(R.isEmpty), value) : R.not(R.isEmpty(value)),
    Object.values(validation)
  );
  const isEmptyNumber = R.anyPass([R.isNil, R.isEmpty, Number.isNaN, R.equals(0)]);
  const isInvalidVolume = value => isEmptyNumber(value) || String(value).length > 14;
  const productsInvalid = customerAuth.products?.some(
    ({ authorizationType, authorizationVolume, renewalVolume }) =>
      R.cond([
        [() => R.or(R.isNil, R.isEmpty)(authorizationType), R.T],
        [
          () =>
            !['LIMITED', 'ONELOAD', 'ONETIME', 'UNLIMITED', 'ONEVOL'].includes(authorizationType),
          () => isInvalidVolume(authorizationVolume) || isInvalidVolume(renewalVolume),
        ],
        [() => R.equals('LIMITED', authorizationType), () => isInvalidVolume(authorizationVolume)],
        [R.T, R.F],
      ])()
  );
  const terminalError = R.isEmpty(R.prop('terminals', customerAuth));
  const productError = R.or(
    R.and(
      R.equals(R.prop('authorizationType', customerAuth), 'SPECIFIC'),
      R.isEmpty(R.prop('products', customerAuth))
    ),
    productsInvalid
  );
  const controlNumberError = R.isEmpty(R.prop('controlNumbers', customerAuth));
  const carrierError = R.and(
    R.equals('true', R.prop('isCarrierRestricted', customerAuth)),
    R.isEmpty(R.prop('carriers', customerAuth))
  );
  const hasErrors = R.any(R.equals(true), [
    hasValidationErrors,
    terminalError,
    productError,
    controlNumberError,
    carrierError,
  ]);
  const enableSave = !R.equals(initialCustomerAuth, customerAuth) && !hasErrors;

  const closeDrawer = () => {
    setCustomerAuth({});
    onClose();
  };

  const handleClose = () => {
    if (!R.equals(initialCustomerAuth, customerAuth)) {
      setOpenDiscardDialog(true);
      return;
    }

    closeDrawer();
  };

  const handleDiscard = () => {
    setOpenDiscardDialog(false);
    closeDrawer();
  };

  const carriers = R.defaultTo([], R.prop('carriers', customerAuth));
  const controlNumbers = R.defaultTo([], R.prop('controlNumbers', customerAuth));
  const products = R.defaultTo([], R.prop('products', customerAuth));
  const terminals = R.defaultTo([], R.prop('terminals', customerAuth));
  const body =
    isEditMode &&
    R.not(R.isEmpty(customerAuth)) &&
    R.omit(['customerClassName', 'isCarrierRestricted', 'remainingVolume'], {
      ...customerAuth,
      authorizationId: R.prop('customerAuthorizationId', customerAuth),
      carriers: R.map(R.prop('carrierId'), carriers),
      controlNumbers: R.map(
        ({ idName, itn, destinationIdName }) => ({ destinationIdName, idName, itn }),
        controlNumbers
      ),
      customerClassId: R.prop('customerClassId', customerAuth),
      products: R.map(
        R.omit(['productIdName', 'productNameShort', 'isProprietary', 'volumeUOM']),
        products
      ),
      supplier: {
        companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
        dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
      },
      terminals: R.map(R.prop('terminalId'), terminals),
    });

  const handleSave = () => {
    submit({ body })
      .then(result => {
        if (result.error) {
          R.flatten(Object.values(result.error.data.errors)).forEach(message => {
            dispatch(createSnack({ message, options: { variant: 'error' } }));
          });
          return;
        }

        const { failureMessages, message } = result.data;
        failureMessages?.forEach(failureMessage => {
          dispatch(createSnack({ message: failureMessage, options: { autoHideDuration: 7500 } }));
        });
        if (message) {
          dispatch(createSnack({ message, options: { autoHideDuration: 7500 } }));
          onSaveSuccess?.();
        }
      })
      .finally(() => {
        setCustomerAuth({});
        setTimeout(onClose, 500);
      });
  };

  return {
    customerAuth,
    enableSave,
    handleClose,
    handleDiscard,
    handleSave,
    hasErrors,
    isSaving,
    openDiscardDialog,
    setCustomerAuth,
    setOpenDiscardDialog,
    setValidation,
    validation,
  };
};

export default useCustomerAuthorizationEditor;
